// FILE: src/pages/admin/staff/AdminRolesPage.jsx
// Roles (RBAC; roles.manage, or staff.manage to read them): each role's
// name, what it is for, whether it is built in (icon + words), how many
// staff have it and how many permissions it gives. "New role" needs
// roles.manage. A role opens on its own page (AdminRoleEditPage).
//   GET /api/admin/roles → { roles: [{ id, name, description, permissions, system, key, members }] }
import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Alert, Box, Button, Link, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, StatusChip } from "../../../components/ui";
import { ResponsiveTable } from "../../../components/admin/AdminBits";
import { PhoneCard, PhoneList, useIsPhone } from "../../../components/admin/PhoneList";
import { StaffRolesNav } from "../../../components/admin/staffBits";
import { ALL_PERMISSIONS } from "../../../auth/permissions";
import usePermissions from "../../../hooks/usePermissions";
import { useRoles } from "../../../services/roles";

export function BuiltInChip({ role }) {
  return role.system ? (
    <StatusChip tone="info" icon={<ShieldOutlinedIcon />} label="Built-in" />
  ) : (
    <StatusChip tone="default" icon={<TuneOutlinedIcon />} label="Custom" />
  );
}

export function permissionCount(role) {
  if (role.key === "ADMINISTRATOR") return "Every permission";
  const n = Array.isArray(role.permissions) ? role.permissions.length : 0;
  return `${n} of ${ALL_PERMISSIONS.length} permissions`;
}

export const membersText = (n) => (Number(n) === 1 ? "1 staff member" : `${Number(n) || 0} staff members`);

export default function AdminRolesPage() {
  const location = useLocation();
  const [notice, setNotice] = React.useState(location.state?.notice || "");
  const { roles, error, reload } = useRoles();
  const { can } = usePermissions();
  const canManage = can("roles.manage");
  const phone = useIsPhone();

  const newRole = canManage ? (
    <Button component={RouterLink} to="/admin/roles/new" variant="contained" startIcon={<AddIcon />}>
      New role
    </Button>
  ) : null;

  return (
    <Box>
      <PageHeader
        title="Roles"
        description="A role is a set of permissions. Each staff member has one role, and it decides which pages and actions they can use. Administrator always has every permission."
        actions={newRole}
      />
      <StaffRolesNav current="/admin/roles" />

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {!canManage && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          You can see the roles to choose one for a staff member. Changing roles needs the “Manage roles” permission.
        </Alert>
      )}

      <Section id="roles" title="All roles" contentSx={{ p: { xs: 2, sm: 3 } }}>
        {roles === null && error ? (
          <ErrorState error={error} title="We couldn't load the roles" onRetry={reload} />
        ) : roles === null ? (
          <LoadingState variant="table" rows={5} label="Loading roles…" />
        ) : roles.length === 0 ? (
          <EmptyState icon={AdminPanelSettingsOutlinedIcon} title="No roles yet" description="The built-in roles appear here once the server has started." action={newRole} />
        ) : phone ? (
          <PhoneList label="Roles">
            {roles.map((r) => (
              <PhoneCard key={r.id}>
                <Link component={RouterLink} to={`/admin/roles/${r.id}`} sx={{ fontWeight: 650 }}>
                  {r.name}
                </Link>
                {r.description && <Typography sx={{ color: "text.secondary" }}>{r.description}</Typography>}
                <Box>
                  <BuiltInChip role={r} />
                </Box>
                <Typography>
                  {membersText(r.members)} · {permissionCount(r)}
                </Typography>
              </PhoneCard>
            ))}
          </PhoneList>
        ) : (
          <ResponsiveTable label="Roles" minWidth={760}>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  Role
                </TableCell>
                <TableCell component="th" scope="col">
                  Kind
                </TableCell>
                <TableCell component="th" scope="col">
                  Staff
                </TableCell>
                <TableCell component="th" scope="col">
                  Permissions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell component="th" scope="row" sx={{ maxWidth: 420 }}>
                    <Link component={RouterLink} to={`/admin/roles/${r.id}`} sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Link>
                    {r.description && <Typography sx={{ color: "text.secondary" }}>{r.description}</Typography>}
                  </TableCell>
                  <TableCell>
                    <BuiltInChip role={r} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{membersText(r.members)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{permissionCount(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        )}
      </Section>
    </Box>
  );
}
